import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, CheckCircle2, XCircle, Trash2, CloudSun, CloudOff, RefreshCw } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { cacheGet, cacheSet, queueAll } from "@/lib/offline";
import { useOffline } from "@/context/OfflineContext";
import { fmtNum, fmtMoney, fmtDate, today } from "@/lib/format";
import { Kpi, Panel, Badge2, PageHead } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { useFormDraft } from "@/lib/drafts";
import { DraftPrompt, DraftRestoredBanner } from "@/components/DraftPrompt";

const BLANK = {
  activity_id: "", date: today(), qty: "", shift: "Day", shift_hours: 8, skilled: 0, semi_skilled: 0,
  unskilled: 0, planned_manpower: 0, idle_hours: 0, weather: "Clear", site_condition: "Normal",
  remarks: "", signature: "",
};

export default function DPR() {
  const { pid } = useParams();
  const { user } = useAuth();
  const { online, pending, syncing, enqueue, sync } = useOffline();
  const [rows, setRows] = useState([]);
  const [acts, setActs] = useState([]);
  const [queued, setQueued] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);
  const draft = useFormDraft({
    formId: `dpr:${pid}`, form, setForm, blank: BLANK, onRestore: () => setOpen(true),
  });

  const loadQueue = () => {
    queueAll().then((all) => setQueued(all.filter((q) => q.project_id === pid))).catch(() => {});
  };

  const load = () => {
    loadQueue();
    api
      .get("/dpr", { params: { project_id: pid } })
      .then(({ data }) => { setRows(data); cacheSet(`dpr:${pid}`, data); })
      .catch(() => cacheGet(`dpr:${pid}`).then((c) => c && setRows(c)));
    api
      .get(`/projects/${pid}/wbs`)
      .then(({ data }) => {
        const list = data.filter((a) => a.total_qty > 0);
        setActs(list);
        cacheSet(`acts:${pid}`, list);
      })
      .catch(() => cacheGet(`acts:${pid}`).then((c) => c && setActs(c)));
  };
  useEffect(() => { load(); }, [pid]);
  useEffect(() => { loadQueue(); }, [pending]);

  const selected = acts.find((a) => a.id === form.activity_id);
  const stats = useMemo(() => ({
    total: rows.length,
    todayCount: rows.filter((r) => r.date === today()).length,
    pending: rows.filter((r) => r.status === "submitted").length,
    value: rows.reduce((s, r) => s + (r.value || 0), 0),
    manpower: rows.filter((r) => r.date === today()).reduce((s, r) => s + r.skilled + r.semi_skilled + r.unskilled, 0),
  }), [rows]);

  const save = async () => {
    const body = {
      project_id: pid, ...form, qty: Number(form.qty), shift_hours: Number(form.shift_hours),
      skilled: Number(form.skilled), semi_skilled: Number(form.semi_skilled), unskilled: Number(form.unskilled),
      planned_manpower: Number(form.planned_manpower), idle_hours: Number(form.idle_hours),
      signature: form.signature || user?.name || "Site Engineer", photos: [],
    };
    if (!online) {
      await enqueue(body);
      toast.success("No signal — entry saved on this device and will sync automatically");
      setOpen(false);
      setForm(BLANK);
      draft.clear();
      loadQueue();
      return;
    }
    try {
      await api.post("/dpr", body);
      toast.success("DPR logged — balance qty, S-curve and EVM updated");
      setOpen(false);
      setForm(BLANK);
      draft.clear();
      load();
    } catch (e) {
      if (!e.response) {
        await enqueue(body);
        toast.success("Network unreachable — entry queued offline for auto-sync");
        setOpen(false);
        setForm(BLANK);
        draft.clear();
        loadQueue();
        return;
      }
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/dpr/${id}/status?status=${status}`); toast.success(`DPR ${status}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/dpr/${id}`); toast.success("Entry deleted"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="dpr-page">
      <PageHead title="Daily Progress Report (L4 Field Log)" subtitle="Enter work done. Everything downstream recalculates automatically.">
        <Button onClick={() => setOpen(true)} data-testid="dpr-new-btn"><Plus className="w-4 h-4 mr-1.5" /> New DPR entry</Button>
      </PageHead>

      <DraftPrompt draft={draft} label="DPR entry" />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-3">
        <Kpi label="Entries logged" value={stats.total} testId="kpi-dpr-total" />
        <Kpi label="Logged today" value={stats.todayCount} tone="primary" testId="kpi-dpr-today" />
        <Kpi label="Awaiting validation" value={stats.pending} tone={stats.pending ? "warn" : "good"} />
        <Kpi label="Value executed" value={fmtMoney(stats.value)} tone="good" />
        <Kpi label="Manpower on site today" value={stats.manpower} />
      </div>

      {!online || queued.length ? (
        <div
          className="flex flex-wrap items-center gap-2 border border-[#F97316]/40 bg-[#F97316]/10 px-3 py-2 mb-3 text-[12px]"
          data-testid="offline-queue-bar"
        >
          <CloudOff className="w-3.5 h-3.5 text-[#F97316] shrink-0" />
          <span data-testid="offline-queue-text">
            {online ? "Back online — " : "Working offline. "}
            <strong>{queued.length}</strong> {queued.length === 1 ? "entry" : "entries"} held on this device.
          </span>
          {online && queued.length ? (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto h-7"
              onClick={async () => { await sync(); load(); }}
              disabled={syncing}
              data-testid="offline-sync-btn"
            >
              <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Syncing…" : "Sync now"}
            </Button>
          ) : null}
        </div>
      ) : null}

      <Panel title="Field log register" testId="dpr-table-panel">
        <div className="overflow-x-auto max-h-[65vh]">
          <table className="w-full dense-table">
            <thead className="sticky-head">
              <tr>
                <th>Date</th><th>Activity</th><th className="text-right">Qty</th><th>Unit</th>
                <th className="text-right">Value</th><th>Shift</th><th className="text-right">Sk</th>
                <th className="text-right">Semi</th><th className="text-right">Un</th><th className="text-right">Plan MP</th>
                <th className="text-right">Idle hrs</th><th>Weather</th><th>By</th><th>Status</th><th />
              </tr>
            </thead>
            <tbody>
              {queued.map((q) => (
                <tr key={q.client_id} className="bg-[#F97316]/5" data-testid={`dpr-queued-${q.client_id}`}>
                  <td className="num">{fmtDate(q.date)}</td>
                  <td className="max-w-[220px] truncate">{acts.find((a) => a.id === q.activity_id)?.name || "—"}</td>
                  <td className="num text-right font-medium">{fmtNum(q.qty, 1)}</td>
                  <td className="text-muted-foreground">{acts.find((a) => a.id === q.activity_id)?.unit || ""}</td>
                  <td className="num text-right text-muted-foreground">—</td>
                  <td>{q.shift}</td>
                  <td className="num text-right">{q.skilled}</td>
                  <td className="num text-right">{q.semi_skilled}</td>
                  <td className="num text-right">{q.unskilled}</td>
                  <td className="num text-right text-muted-foreground">{q.planned_manpower}</td>
                  <td className="num text-right text-amber-500">{q.idle_hours}</td>
                  <td className="text-muted-foreground">{q.weather}</td>
                  <td className="text-muted-foreground text-[11px]">{q.signature}</td>
                  <td><Badge2 tone="warn">pending sync</Badge2></td>
                  <td />
                </tr>
              ))}
              {rows.map((r) => (
                <tr key={r.id} data-testid={`dpr-row-${r.id}`}>
                  <td className="num">{fmtDate(r.date)}</td>
                  <td className="max-w-[220px] truncate" title={r.activity_name}>{r.activity_name}</td>
                  <td className="num text-right font-medium">{fmtNum(r.qty, 1)}</td>
                  <td className="text-muted-foreground">{r.unit}</td>
                  <td className="num text-right">{fmtMoney(r.value)}</td>
                  <td>{r.shift}</td>
                  <td className="num text-right">{r.skilled}</td>
                  <td className="num text-right">{r.semi_skilled}</td>
                  <td className="num text-right">{r.unskilled}</td>
                  <td className="num text-right text-muted-foreground">{r.planned_manpower}</td>
                  <td className="num text-right text-amber-500">{r.idle_hours}</td>
                  <td className="text-muted-foreground">{r.weather}</td>
                  <td className="text-muted-foreground text-[11px]">{r.submitted_by_name || r.submitted_by}</td>
                  <td>
                    <Badge2 tone={r.status === "validated" ? "good" : r.status === "rejected" ? "bad" : "warn"}>{r.status}</Badge2>
                  </td>
                  <td>
                    <div className="flex items-center gap-1.5">
                      <button onClick={() => setStatus(r.id, "validated")} className="text-muted-foreground hover:text-emerald-500" title="Validate" data-testid={`dpr-validate-${r.id}`}><CheckCircle2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => setStatus(r.id, "rejected")} className="text-muted-foreground hover:text-amber-500" title="Reject" data-testid={`dpr-reject-${r.id}`}><XCircle className="w-3.5 h-3.5" /></button>
                      <button onClick={() => remove(r.id)} className="text-muted-foreground hover:text-red-500" title="Delete" data-testid={`dpr-delete-${r.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 ? <tr><td colSpan={15} className="text-muted-foreground">No DPR entries yet.</td></tr> : null}
            </tbody>
          </table>
        </div>
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Daily progress entry</DialogTitle>
            <DialogDescription>Log executed quantity and manpower for one activity. Saved on this device if you are offline.</DialogDescription>
          </DialogHeader>
          <DraftRestoredBanner draft={draft} />
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="sm:col-span-2 space-y-1.5">
              <Label className="text-xs uppercase">Activity</Label>
              <Select value={form.activity_id} onValueChange={(v) => setForm({ ...form, activity_id: v })}>
                <SelectTrigger data-testid="dpr-activity-select"><SelectValue placeholder="Select activity" /></SelectTrigger>
                <SelectContent>
                  {acts.map((a) => <SelectItem key={a.id} value={a.id}>{a.code} · {a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="dpr-date-input" /></div>
            {selected ? (
              <div className="sm:col-span-3 border border-border bg-muted/40 p-2.5 grid grid-cols-3 gap-2 text-[12px]">
                <div><span className="text-muted-foreground">Balance available </span><span className="num font-semibold">{fmtNum(selected.balance_qty, 1)} {selected.unit}</span></div>
                <div><span className="text-muted-foreground">Progress </span><span className="num font-semibold">{selected.progress_pct}%</span></div>
                <div><span className="text-muted-foreground">Run-rate required </span><span className="num font-semibold">{fmtNum(selected.run_rate_required, 1)} {selected.unit}/day</span></div>
              </div>
            ) : null}
            <div className="space-y-1.5"><Label className="text-xs uppercase">Qty executed {selected ? `(${selected.unit})` : ""}</Label>
              <Input value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} data-testid="dpr-qty-input" /></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Shift</Label>
              <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                <SelectTrigger data-testid="dpr-shift-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Day", "Night", "General"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Shift hours</Label>
              <Input value={form.shift_hours} onChange={(e) => setForm({ ...form, shift_hours: e.target.value })} data-testid="dpr-hours-input" /></div>
            {[["skilled", "High-skilled"], ["semi_skilled", "Semi-skilled"], ["unskilled", "Unskilled"],
              ["planned_manpower", "Planned manpower"], ["idle_hours", "Idle hours"]].map(([k, l]) => (
              <div key={k} className="space-y-1.5"><Label className="text-xs uppercase">{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`dpr-${k}-input`} /></div>
            ))}
            <div className="space-y-1.5"><Label className="text-xs uppercase">Weather</Label>
              <Select value={form.weather} onValueChange={(v) => setForm({ ...form, weather: v })}>
                <SelectTrigger data-testid="dpr-weather-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Clear", "Rain", "Hot", "Windy", "Fog"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Site condition</Label>
              <Select value={form.site_condition} onValueChange={(v) => setForm({ ...form, site_condition: v })}>
                <SelectTrigger data-testid="dpr-condition-select"><SelectValue /></SelectTrigger>
                <SelectContent>{["Normal", "Congested", "Wet", "Restricted Access"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="space-y-1.5"><Label className="text-xs uppercase">Digital signature</Label>
              <Input placeholder={user?.name} value={form.signature} onChange={(e) => setForm({ ...form, signature: e.target.value })} data-testid="dpr-signature-input" /></div>
            <div className="sm:col-span-3 space-y-1.5"><Label className="text-xs uppercase">Remarks / site conditions</Label>
              <Textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} data-testid="dpr-remarks-input" /></div>
          </div>
          <DialogFooter>
            <span className="text-[11px] text-muted-foreground mr-auto flex items-center gap-1.5"><CloudSun className="w-3.5 h-3.5" /> Entry is validated against remaining scope.</span>
            <Button onClick={save} disabled={!form.activity_id || !form.qty} data-testid="dpr-save-btn">Submit DPR</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
